import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, SettingsIcon } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TRANSACTIONS_SETTINGS_ANCHOR } from '@modules/_shared/components/report-editor/preview/previewAnchors';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useTourReveal } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import {
  UPDATE_CMA_REPORT_ADJUST_PRICE,
  UPDATE_CMA_REPORT_TRANSACTIONS_SQUARE_METER_CALCULATION,
  UPDATE_CMA_REPORT_TRANSACTION_MAP_ZOOM_LEVEL,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import ZoomLevelSlider from '@modules/reports/shared/ZoomLevelSlider';
import { useLocale } from '@shared/hooks/locale';

type TransactionSettingsProps = {
  adjustPriceByEvolution: boolean;
  isPropertyTransactionSquareMeterCalculation: boolean;
  transactionMapZoomLevel: number | null;
};

function TransactionSettings({
  adjustPriceByEvolution,
  isPropertyTransactionSquareMeterCalculation,
  transactionMapZoomLevel,
}: TransactionSettingsProps): React.ReactElement {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  // The DVF tour points at a setting inside: open the panel.
  useTourReveal(TRANSACTIONS_SETTINGS_ANCHOR, () => setExpanded(true));
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data: cmaReportData } = useReportContext();

  const displayedZoomLevel = transactionMapZoomLevel ?? 15;

  const [updateAdjustPrice, { loading: loadingAdjustPrice }] = useMutation(
    UPDATE_CMA_REPORT_ADJUST_PRICE,
    { onError: setErrorLike },
  );

  const [
    updateSquareMeterCalculation,
    { loading: loadingSquareMeterCalculation },
  ] = useMutation(UPDATE_CMA_REPORT_TRANSACTIONS_SQUARE_METER_CALCULATION, {
    onError: setErrorLike,
  });

  const [updateMapZoomLevel] = useMutation(
    UPDATE_CMA_REPORT_TRANSACTION_MAP_ZOOM_LEVEL,
    { onError: setErrorLike },
  );

  const handleSquareMeterCalculationToggle = useCallback(
    async (value: boolean) => {
      await updateSquareMeterCalculation({
        variables: {
          id: cmaReportData?.id,
          is_property_transaction_square_meter_calculation: value,
        },
      });
    },
    [cmaReportData?.id, updateSquareMeterCalculation],
  );

  const handleAdjustPriceToggle = useCallback(
    async (value: boolean) => {
      await updateAdjustPrice({
        variables: {
          id: cmaReportData?.id,
          adjust_price_by_evolution: value,
        },
      });
    },
    [cmaReportData?.id, updateAdjustPrice],
  );

  const handleMapZoomLevelChange = useCallback(
    async (value: number) => {
      await updateMapZoomLevel({
        variables: {
          id: cmaReportData?.id,
          transaction_map_zoom_level: value,
        },
      });
    },
    [cmaReportData?.id, updateMapZoomLevel],
  );

  return (
    <>
      <Box
        {...tourAnchor(TRANSACTIONS_SETTINGS_ANCHOR)}
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
            px: 2,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <SettingsIcon
              size={20}
              color={'var(--mui-palette-text-secondary)'}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
              }}
            >
              {t('Settings')}
            </Typography>
            {!expanded && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {adjustPriceByEvolution && `• ${t('Price adjustment')}`}
                {isPropertyTransactionSquareMeterCalculation &&
                  ` • ${t('Square meter')}`}
                {` • ${t('Zoom')}: ${displayedZoomLevel}`}
              </Typography>
            )}
          </Stack>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? (
              <ChevronUpIcon size={20} />
            ) : (
              <ChevronDownIcon size={20} />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Stack sx={{ p: 1, gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={adjustPriceByEvolution}
                  onChange={e => handleAdjustPriceToggle(e.target.checked)}
                  disabled={loadingAdjustPrice}
                />
              }
              label={t('Adjust price by evolution')}
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isPropertyTransactionSquareMeterCalculation}
                  onChange={e =>
                    handleSquareMeterCalculationToggle(e.target.checked)
                  }
                  disabled={loadingSquareMeterCalculation}
                />
              }
              label={t('Use square meter calculation')}
              sx={{ m: 0 }}
            />

            <ZoomLevelSlider
              value={transactionMapZoomLevel}
              defaultValue={15}
              onChange={handleMapZoomLevelChange}
              label={t('Map zoom level')}
            />
          </Stack>
        </Collapse>
      </Box>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
}

export default TransactionSettings;
